import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { catchError, EMPTY, Subject, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators'
import { ToasterService } from '@shared/services/toaster'
import { CurrentOrganization } from '@shared/services/current-organization'
import { CurrentUser } from '@shared/services/current-user/current-user'
import { GetClientChatQuery, IClientChatResponse } from '@app/support-module/core/queries/get-client-chat.query'
import { GetWorkingHoursQuery } from '@app/support-module/core/queries/get-working-hours.query'
import { SendSupportMessageCommand } from '@app/support-module/core/commands/send-support-message.command'
import { MarkClientChatAsReadCommand } from '@app/support-module/core/commands/mark-client-chat-as-read.command'
import { SupportWidgetMessagesService } from '@app/support-module/core/services/support-widget-messages.service'
import { IChatMessage } from '@app/support-module/core/types'

/**
 * Плавающий виджет чата поддержки. Монтируется в `app.component` для авторизованного пользователя
 * с активной организацией. При открытии подтягивает / создаёт чат и подписывается на WS.
 */
@Component({
  selector: 'support-widget',
  templateUrl: './support-widget.component.html',
  styleUrls: ['./support-widget.component.scss'],
  standalone: false,
  providers: [SupportWidgetMessagesService],
})
export class SupportWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>
  @ViewChild('textarea') textareaRef?: ElementRef<HTMLTextAreaElement>

  private readonly destroyed$ = new Subject<void>()
  private readonly markAsReadTrigger$ = new Subject<number>()
  private markAsReadSub?: Subscription
  private shouldScrollToBottom = false
  private lastReadMessageId: number | null = null

  isOpen = false
  isBooting = false
  isSending = false
  chat: IClientChatResponse | null = null
  messages: IChatMessage[] = []
  loading = false
  body = ''
  workingHours: string | null = null

  constructor(
    private readonly currentUser: CurrentUser,
    private readonly currentOrganization: CurrentOrganization,
    private readonly getChat: GetClientChatQuery,
    private readonly getWorkingHours: GetWorkingHoursQuery,
    private readonly sendMessage: SendSupportMessageCommand,
    private readonly markAsRead: MarkClientChatAsReadCommand,
    private readonly toaster: ToasterService,
    readonly messagesService: SupportWidgetMessagesService
  ) {}

  ngOnInit(): void {
    this.messagesService.messages$.pipe(takeUntil(this.destroyed$)).subscribe((messages) => {
      const prevLastId = this.messages[this.messages.length - 1]?.id
      this.messages = messages
      const newLastId = messages[messages.length - 1]?.id
      if (newLastId !== prevLastId) {
        this.shouldScrollToBottom = true
        if (newLastId && this.isOpen) this.markAsReadTrigger$.next(newLastId)
      }
    })

    this.messagesService.loading$.pipe(takeUntil(this.destroyed$)).subscribe((l) => (this.loading = l))

    this.markAsReadSub = this.markAsReadTrigger$
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        filter((id) => id !== this.lastReadMessageId && !!this.currentOrganization.id),
        switchMap((id) => {
          this.lastReadMessageId = id
          return this.markAsRead.call(this.currentOrganization.id, id)
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe({ error: (err) => console.warn('[support-widget] mark_as_read failed', err) })
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom && this.scrollContainer) {
      const el = this.scrollContainer.nativeElement
      el.scrollTop = el.scrollHeight
      this.shouldScrollToBottom = false
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
    this.markAsReadTrigger$.complete()
    this.markAsReadSub?.unsubscribe()
  }

  toggle(): void {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open(): void {
    this.isOpen = true
    if (!this.chat) {
      this.boot()
    }
  }

  close(): void {
    this.isOpen = false
  }

  get canSend(): boolean {
    return !this.isSending && this.body.trim().length > 0 && !!this.chat
  }

  handleEnter(event: Event): void {
    event.preventDefault()
    this.send()
  }

  preventShiftEnter(_event: Event): void {
    // Перенос строки делает textarea по умолчанию.
  }

  onBodyInput(event: Event): void {
    this.body = (event.target as HTMLTextAreaElement).value
  }

  send(): void {
    const body = this.body.trim()
    if (!this.canSend || !body || !this.currentOrganization.id) return
    this.isSending = true
    this.sendMessage
      .call(this.currentOrganization.id, body)
      .pipe(
        catchError((err) => {
          this.toaster.errorHTTP(err)
          this.isSending = false
          return EMPTY
        })
      )
      .subscribe((message) => {
        this.isSending = false
        this.body = ''
        this.messagesService.appendLocal(message)
      })
  }

  isOwnMessage(message: IChatMessage): boolean {
    return message.created_by_side === 'user'
  }

  private boot(): void {
    const orgId = this.currentOrganization.id
    if (!orgId || this.isBooting) return
    this.isBooting = true

    this.getChat
      .call(orgId)
      .pipe(
        catchError((err) => {
          this.toaster.errorHTTP(err)
          this.isBooting = false
          return EMPTY
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((chat) => {
        this.chat = chat
        this.isBooting = false
        this.messagesService.load(orgId, chat.id)
        setTimeout(() => this.textareaRef?.nativeElement.focus(), 0)
      })

    if (this.workingHours === null) {
      this.getWorkingHours
        .call()
        .pipe(
          catchError(() => EMPTY),
          takeUntil(this.destroyed$)
        )
        .subscribe((wh) => (this.workingHours = wh.text))
    }
  }
}
